import './ReceptionShow.scss';

import { DateField, Show, TextField } from '@refinedev/antd';
import { Alert, Divider, Tag } from 'antd';
import { useTranslation } from 'react-i18next';

import { useCountryEnforcer } from '@/components/general/CountryEnforcer';
import LinkTextField from '@/components/general/LinkTextField';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { RESOURCES } from '@/Resources';

import ReceptionShowContactInfo from './ReceptionShow/ReceptionShowContactInfo';
import ReceptionShowDetail from './ReceptionShow/ReceptionShowDetail';
import ReceptionShowFiles from './ReceptionShow/ReceptionShowFiles';
import ReceptionShowHeaderButtons from './ReceptionShow/ReceptionShowHeaderButtons';
import ReceptionShowLogistic from './ReceptionShow/ReceptionShowLogistic';
import ReceptionShowReturnAddress from './ReceptionShow/ReceptionShowReturnAddress';
import ReceptionShowTechnician from './ReceptionShow/ReceptionShowTechnician';
import useReceptionShow from './ReceptionShow/useReceptionShow';

export const ReceptionShow = () => {
  const { alert, blockEditForCountry } = useCountryEnforcer();
  const { t } = useTranslation();
  const {
    record,
    loading,
    refetchResource,
    blockChangeState,
    setBlockChangeState,
    canFinishReception,
    canMarkAsSendingReception,
    canEditResolution,
    createdBy,
    ticket,
    companyContact,
    serviceOrders,
    packageItems,
    setPackageItems,
    itemsEditMode,
  } = useReceptionShow();

  return (
    <Show
      isLoading={loading}
      headerButtons={buttonProps =>
        record && (
          <ReceptionShowHeaderButtons
            record={record}
            refineButtonsProps={buttonProps}
            refetch={() => void refetchResource()}
            blockChangeState={blockChangeState}
            canFinishReception={canFinishReception}
            canMarkAsSendingReception={canMarkAsSendingReception}
            blockForCountry={blockEditForCountry}
          />
        )
      }>
      {alert}
      <div className="ReceptionShow">
        <div className="ReceptionShowDataRow">
          <div className="ReceptionShowData ReceptionShowDataId">
            <b>
              {t('Id de recepción')}: {record?.id}
            </b>
            {record?.status != undefined && (
              <div className="TicketShowRowBooleans">
                <PackageStateTag packageStatus={record?.status} />
                {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
                  <Tag color="geekblue">{t('Automática')}</Tag>
                )}
              </div>
            )}
          </div>

          <div className="ReceptionShowData">
            <div>
              <b>{t('Fecha de creación')}: </b>
              <DateField value={record?.created_at} />
            </div>
            <div>
              <b>{t('Fecha última modificación')}: </b>
              <DateField value={record?.updated_at} />
            </div>

            <div>
              <b>{t('Creado por')}: </b>
              <TextField
                value={
                  createdBy
                    ? `${createdBy?.first_name} ${createdBy?.last_name} (${createdBy?.id})`
                    : ''
                }
              />
            </div>
          </div>
        </div>
        {serviceOrders && serviceOrders.length > 0 && (
          <Alert
            style={{ marginTop: 12 }}
            type="warning"
            showIcon
            message={
              <>
                {t('Existen órdenes de servicio asociadas a esta recepción')}:{' '}
                {serviceOrders.map((so, index) => (
                  <span key={so.id}>
                    {index > 0 && ', '}
                    <LinkTextField
                      label={`${t('Orden de servicio')} #${so.id}`}
                      to={`/${RESOURCES.SERVICE_ORDERS}/show/${so.id}`}
                      external={true}
                    />
                  </span>
                ))}
              </>
            }
          />
        )}
        {record?.status == PACKAGE_STATES.CANCELADO && record?.cancellation_motive && (
          <ShowSection title={t('Motivo de cancelación')}>
            <TextField value={record?.cancellation_motive} />
          </ShowSection>
        )}
        <ShowSection title={t('Detalles de la recepción')}>
          <ReceptionShowDetail
            record={record}
            ticket={ticket}
            refetch={() => void refetchResource()}
          />
        </ShowSection>

        {record?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <>
            <ShowSection>
              <ReceptionShowContactInfo companyContact={companyContact} />
            </ShowSection>
            <ShowSection title={t('Dirección de devolución')} className="ReceptionShowSection">
              <ReceptionShowReturnAddress record={record} />
            </ShowSection>
          </>
        )}
        <ShowSection title={t('Logística')}>
          <ReceptionShowLogistic record={record} refetch={() => void refetchResource()} />
        </ShowSection>
        {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <ShowSection>
            <ReceptionShowTechnician record={record} refetch={() => void refetchResource()} />
          </ShowSection>
        )}
        <ShowSection title={t('Items')} withoutCard>
          <PackageItemsList
            packageItems={packageItems}
            setPackageItems={setPackageItems}
            editMode={itemsEditMode}
            package={{ id: record?.id }}
            setBlockChangeState={setBlockChangeState}
            canEditResolution={canEditResolution}
            refetchResource={refetchResource}
          />
        </ShowSection>

        {record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
          <Alert
            style={{ marginTop: 16 }}
            showIcon
            type="info"
            message={t(
              'No se pueden modificar los elementos de una recepción creada automáticamente',
            )}
          />
        )}
        {(record?.status == PACKAGE_STATES.RECIBIDO ||
          record?.status == PACKAGE_STATES.FINALIZADO) && (
          <>
            <Divider />
            <ShowSection title={t('Fotos')} withoutCard>
              <ReceptionShowFiles record={record} />
            </ShowSection>
          </>
        )}
      </div>
    </Show>
  );
};
